import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { FacilityVisibility } from "./facility-visibility";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Schema } from "@bw/shared/types/generated";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  getFacilityApprovalReadiness,
  submitFacilityForReview,
  type FacilityApprovalReadinessResponse,
  type FacilityApprovalStatus,
} from "@/libs/facilityApi";

type FacilityResponse = Schema["FacilityResponse"];

export type FacilityApprovalSubject = Pick<
  FacilityResponse,
  "facility_id" | "name" | "approval_status" | "approval_rejection_reason"
>;

export interface FacilityApprovalPanelProps {
  facility: FacilityApprovalSubject;
  onFacilityChange?: (facility: FacilityResponse) => void;
  submissionAllowed?: boolean;
  submissionBlocked?: boolean;
}

export function facilityApprovalReadinessQueryKey(facilityId: string) {
  return ["facility-approval-readiness", facilityId] as const;
}

const STATUS_LABELS: Record<FacilityApprovalStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  approved: "Approved",
  rejected: "Rejected",
};

const STATUS_COPY: Record<FacilityApprovalStatus, string> = {
  draft:
    "Organization approval and facility approval are separate. Complete this facility’s setup, then submit the facility for review.",
  in_review:
    "This facility is not operational while platform review is pending.",
  approved: "Platform approval is complete.",
  rejected:
    "Platform review rejected this facility. Address the reason and blockers, then resubmit.",
};

const STATUS_COLORS: Record<
  FacilityApprovalStatus,
  "warning" | "info" | "success" | "error"
> = {
  draft: "warning",
  in_review: "info",
  approved: "success",
  rejected: "error",
};

function statusIcon(status: FacilityApprovalStatus) {
  if (status === "approved") return <CheckCircleOutlinedIcon />;
  if (status === "in_review") return <ScheduleIcon />;
  if (status === "rejected") return <ErrorOutlineOutlinedIcon />;
  return undefined;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function ApprovalIssues({
  readiness,
}: {
  readiness: FacilityApprovalReadinessResponse;
}) {
  const blockers = readiness.blockers ?? [];
  const warnings = readiness.warnings ?? [];

  if (blockers.length === 0 && warnings.length === 0) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", color: "success.main" }}
      >
        <CheckCircleOutlinedIcon fontSize="small" />
        <Typography variant="body2">
          No facility approval issues reported.
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
      }}
    >
      {blockers.length > 0 ? (
        <Box>
          <Typography
            color="text.secondary"
            variant="overline"
            sx={{ fontWeight: 700 }}
          >
            Blockers
          </Typography>
          <List
            disablePadding
            sx={{ border: 1, borderColor: "error.main", borderRadius: 1.5 }}
          >
            {blockers.map((blocker) => (
              <ListItem
                divider
                key={`${blocker.code}:${blocker.message}`}
                sx={{ alignItems: "flex-start", gap: 1, px: 1.5, py: 1 }}
              >
                <ErrorOutlineOutlinedIcon
                  color="error"
                  fontSize="small"
                  sx={{ mt: 0.25 }}
                />
                <Typography variant="body2">{blocker.message}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}
      {warnings.length > 0 ? (
        <Box>
          <Typography
            color="text.secondary"
            variant="overline"
            sx={{ fontWeight: 700 }}
          >
            Warnings
          </Typography>
          <List
            disablePadding
            sx={{ border: 1, borderColor: "warning.main", borderRadius: 1.5 }}
          >
            {warnings.map((warning) => (
              <ListItem
                divider
                key={`${warning.code}:${warning.message}`}
                sx={{ alignItems: "flex-start", gap: 1, px: 1.5, py: 1 }}
              >
                <WarningAmberIcon
                  color="warning"
                  fontSize="small"
                  sx={{ mt: 0.25 }}
                />
                <Typography variant="body2">{warning.message}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}
    </Box>
  );
}

export function FacilityApprovalPanel({
  facility,
  onFacilityChange,
  submissionAllowed = true,
  submissionBlocked = false,
}: FacilityApprovalPanelProps) {
  const queryClient = useQueryClient();
  const queryKey = facilityApprovalReadinessQueryKey(facility.facility_id);
  const selectedFacilityIdRef = useRef(facility.facility_id);
  selectedFacilityIdRef.current = facility.facility_id;
  const pendingFacilityIdsRef = useRef(new Set<string>());
  const [pendingFacilityIds, setPendingFacilityIds] = useState<
    ReadonlySet<string>
  >(() => new Set());

  function setFacilityPending(facilityId: string, pending: boolean) {
    const nextPendingFacilityIds = new Set(pendingFacilityIdsRef.current);
    if (pending) {
      nextPendingFacilityIds.add(facilityId);
    } else {
      nextPendingFacilityIds.delete(facilityId);
    }
    pendingFacilityIdsRef.current = nextPendingFacilityIds;
    setPendingFacilityIds(nextPendingFacilityIds);
  }

  const readinessQuery = useQuery({
    queryKey,
    queryFn: () => getFacilityApprovalReadiness(facility.facility_id),
    enabled: submissionAllowed,
  });

  const submitMutation = useMutation({
    mutationFn: (requestFacilityId: string) =>
      submitFacilityForReview(requestFacilityId),
    onSuccess: (updated, requestFacilityId) => {
      queryClient.setQueryData<FacilityApprovalReadinessResponse>(
        facilityApprovalReadinessQueryKey(requestFacilityId),
        (current) =>
          current
            ? {
                ...current,
                approval_status: updated.approval_status,
                can_submit: false,
              }
            : current,
      );
      queryClient.setQueryData(
        ["facility", requestFacilityId],
        (current: unknown) =>
          current && typeof current === "object"
            ? {
                ...current,
                approval_status: updated.approval_status,
                approval_submitted_at: updated.approval_submitted_at,
                approval_reviewed_at: updated.approval_reviewed_at,
                approval_rejection_reason: updated.approval_rejection_reason,
              }
            : current,
      );
      void queryClient.invalidateQueries({ queryKey: ["org-facilities"] });
      if (selectedFacilityIdRef.current === requestFacilityId) {
        onFacilityChange?.(updated);
      }
    },
    onSettled: (_data, _error, requestFacilityId) => {
      setFacilityPending(requestFacilityId, false);
    },
  });

  const readiness = readinessQuery.data;
  const status = readiness?.approval_status ?? facility.approval_status;
  const canSubmit =
    submissionAllowed &&
    (status === "draft" || status === "rejected") &&
    readiness !== undefined;
  const actionLabel =
    status === "rejected" ? "Resubmit for review" : "Submit for review";
  const submitResultIsCurrent =
    submitMutation.variables === facility.facility_id;
  const isSubmittingCurrent = pendingFacilityIds.has(facility.facility_id);

  function submitCurrentFacility() {
    const requestFacilityId = facility.facility_id;
    if (pendingFacilityIdsRef.current.has(requestFacilityId)) return;
    setFacilityPending(requestFacilityId, true);
    submitMutation.mutate(requestFacilityId);
  }

  return (
    <>
      <Paper
        aria-labelledby={`facility-approval-${facility.facility_id}`}
        component="section"
        variant="outlined"
        sx={{ overflow: "hidden" }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: "flex-start",
            borderBottom: 1,
            borderColor: "divider",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Box>
            <Typography
              component="h2"
              id={`facility-approval-${facility.facility_id}`}
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Facility approval
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {facility.name}
            </Typography>
          </Box>
          <Chip
            color={STATUS_COLORS[status]}
            icon={statusIcon(status)}
            label={STATUS_LABELS[status]}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Stack spacing={2} sx={{ p: 2 }}>
          <Typography color="text.secondary" variant="body2">
            {STATUS_COPY[status]}
          </Typography>

          {status === "rejected" && facility.approval_rejection_reason ? (
            <Alert severity="error" variant="outlined">
              <Typography
                variant="overline"
                sx={{ display: "block", fontWeight: 700 }}
              >
                Rejection reason
              </Typography>
              <Typography variant="body2">
                {facility.approval_rejection_reason}
              </Typography>
            </Alert>
          ) : null}

          {readinessQuery.isLoading ? (
            <Stack
              direction="row"
              role="status"
              spacing={1}
              sx={{ alignItems: "center" }}
            >
              <CircularProgress size={16} />
              <Typography color="text.secondary" variant="body2">
                Checking approval readiness...
              </Typography>
            </Stack>
          ) : null}

          {readinessQuery.isError ? (
            <Alert
              action={
                <Button
                  color="inherit"
                  onClick={() => void readinessQuery.refetch()}
                  size="small"
                  startIcon={<RefreshIcon />}
                  type="button"
                  variant="outlined"
                >
                  Retry readiness check
                </Button>
              }
              severity="error"
            >
              {errorMessage(
                readinessQuery.error,
                "Unable to load facility approval readiness.",
              )}
            </Alert>
          ) : null}

          {readiness ? <ApprovalIssues readiness={readiness} /> : null}

          {submitMutation.isError && submitResultIsCurrent ? (
            <Alert severity="error">
              {errorMessage(
                submitMutation.error,
                "Unable to submit this facility for review.",
              )}
            </Alert>
          ) : null}

          {canSubmit ? (
            <Box>
              {submissionBlocked ? (
                <Typography
                  color="text.secondary"
                  sx={{ mb: 1 }}
                  variant="body2"
                >
                  Save identity changes before submitting for review.
                </Typography>
              ) : null}
              <Button
                disabled={
                  !readiness.can_submit ||
                  submissionBlocked ||
                  readinessQuery.isFetching ||
                  isSubmittingCurrent
                }
                onClick={submitCurrentFacility}
                size="small"
                startIcon={
                  isSubmittingCurrent ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : (
                    <SendOutlinedIcon />
                  )
                }
                type="button"
                variant="contained"
              >
                {isSubmittingCurrent ? "Submitting..." : actionLabel}
              </Button>
            </Box>
          ) : null}
        </Stack>
      </Paper>
      <FacilityVisibility facilityId={facility.facility_id} />
    </>
  );
}
